/**
 * Auto-lock on inactivity.
 * - Interval (every 30s): while tab is visible, checks elapsed time since last activity and locks if over threshold.
 * - visibilitychange: when tab becomes visible again, also checks elapsed time (e.g. after device wake).
 * Suppresses auto-lock while recording, uploads, or document completion are in progress.
 */

import { useCallback, useEffect, useRef } from "react";
import { useAccountContext } from "@/state/providers/AccountProvider";
import { useImpersonation } from "@/state/providers/ImpersonationProvider";
import { useLock } from "@/state/providers/LockProvider";
import { useAppSelector } from "@/common/hooks/useRedux";
import { useDeviceAccessSettings } from "@/state/hooks/useDeviceAccess";
import { RecordingState } from "@/common/utils/types";

const ACTIVITY_EVENTS = ["mousedown", "keydown", "touchstart", "scroll"] as const;
const IDLE_CHECK_INTERVAL_MS = 30 * 1000; // check every 30s while tab is visible

export function useAutoLockListener() {
    const { accountData } = useAccountContext();
    const { requestLock, isLocked } = useLock();
    const { isImpersonating } = useImpersonation();
    const lastActivityRef = useRef<number>(Date.now());

    const { data: deviceSettings } = useDeviceAccessSettings();

    const pinsEnabled = accountData?.pins_enabled === true;

    const { globalRecordingState, isUploadingGlobal, completing } = useAppSelector((state) => state.globalState);
    const isActive =
        globalRecordingState === RecordingState.recording ||
        globalRecordingState === RecordingState.paused ||
        isUploadingGlobal ||
        completing;

    const isActiveRef = useRef(isActive);
    isActiveRef.current = isActive;

    // Update last activity timestamp on user input
    useEffect(() => {
        const updateActivity = () => {
            lastActivityRef.current = Date.now();
        };
        ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, updateActivity));
        return () => ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, updateActivity));
    }, []);

    const timeoutMinutes = deviceSettings?.auto_lock_timeout_minutes ?? 5;
    const mode = deviceSettings?.device_access_mode;
    const shouldCheckIdle =
        pinsEnabled && (mode === "pin" || mode === "switch") && timeoutMinutes > 0 && !isImpersonating && !isLocked;

    const checkElapsedAndLock = useCallback(() => {
        if (document.visibilityState !== "visible") return;
        if (isActiveRef.current) return; // don't lock while recording, upload, or completing
        const elapsed = (Date.now() - lastActivityRef.current) / 1000 / 60;
        if (elapsed >= timeoutMinutes) {
            requestLock();
        }
    }, [timeoutMinutes, requestLock]);

    // Interval: while tab is visible, check idle time every 30s (tablets in exam rooms)
    useEffect(() => {
        if (!shouldCheckIdle) return;
        const intervalId = setInterval(checkElapsedAndLock, IDLE_CHECK_INTERVAL_MS);
        return () => clearInterval(intervalId);
    }, [shouldCheckIdle, checkElapsedAndLock]);

    // On tab visible / device wake: check elapsed time and auto-lock if over threshold
    useEffect(() => {
        if (!shouldCheckIdle) return;

        const onVisibilityChange = () => {
            checkElapsedAndLock();
        };

        document.addEventListener("visibilitychange", onVisibilityChange);
        return () => document.removeEventListener("visibilitychange", onVisibilityChange);
    }, [shouldCheckIdle, checkElapsedAndLock]);
}
